import {
	Container,
	ContainerPadding,
	CTA,
	Spinner,
	styled,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { ScopedDisclaimerManager } from '@volkswagen-onehub/disclaimer-manager';
import { Close } from '@volkswagen-onehub/icons-core';
import {
	FocusLayerSizeV2,
	LayerHandleV2,
	ShimBackgroundTypeV2,
} from '@volkswagen-onehub/layer-manager';
import React from 'react';
import { OfertasContext } from '../../context/ofertas-context';
import { formatSummaries } from '../../helpers/calculatorFunctions';
import {
	checkActiveDisclaimer,
	formatCalculatedDisclaimer,
} from '../../helpers/get-disclaimer';
import {
	CalculatorDefaultResponse,
	CalculatorFormatedSummaries,
} from '../../hooks/calculator/useCalculator';
import {
	FinanceData,
	Oferta,
} from '../../services/ofertas-service/models/ofertas';

export const FinanceLayer: React.FC<{
	financeData: FinanceData;
	setIsFinanceLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	isFinanceLayerVisible: boolean;
	financeLayerRef: React.RefObject<LayerHandleV2<any>>;
	setIsLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
	financeData,
	setIsFinanceLayerVisible,
	isFinanceLayerVisible,
	financeLayerRef,
	setIsLayerVisible,
}) => {
	const { layerManager, calculatorResult, ofertasService, disclaimerManager } =
		React.useContext(OfertasContext);
	const ofertasContext = React.useContext(OfertasContext);
	const calculatorContainerRef = React.useRef<HTMLDivElement | null>(null);
	const rawResponseRef =
		React.useRef<CalculatorDefaultResponse['rawResponse']>();
	const summariesRef = React.useRef<CalculatorFormatedSummaries>();
	const scopedDisclaimerManager =
		disclaimerManager.getScopedDisclaimerManager();

	React.useEffect(() => {
		if (
			calculatorResult?.status === 'ready' &&
			calculatorContainerRef.current &&
			financeData.request &&
			financeData.selectedOferta
		) {
			const selectedOferta = financeData.selectedOferta;
			const { calculator } = calculatorResult;

			//Si anteriormente se calculo obtuvo el legal de una oferta calculada se guarda aqui
			const calculatedDisclamerBeforeUpdate = checkActiveDisclaimer(
				scopedDisclaimerManager.getFooterDisclaimers().reference,
				formatCalculatedDisclaimer(selectedOferta),
			);

			const spinner = calculatorContainerRef.current.querySelector('.spinner');
			if (spinner) {
				spinner.remove();
			}

			calculator.getInterface(financeData.request, {
				ResultContainer: calculatorContainerRef.current,
				Layout: 'auto',
				Summary: {
					Type: 'Full',
				},
				AutoPostback: true,
				ApplyButton: 'Aplicar',
				ResponseCallback: (defaultsResponse) => {
					const response = defaultsResponse as CalculatorDefaultResponse;
					rawResponseRef.current = response.rawResponse;
					summariesRef.current = formatSummaries(response.rawResponse);
				},
				ApplyButtonFunction: (product) => {
					selectedOferta.calculatedFinance.rawResponse = rawResponseRef.current;
					selectedOferta.calculatedFinance.summaries = summariesRef.current;

					if (calculatedDisclamerBeforeUpdate == undefined) {
						addCalculatedReference(
							selectedOferta,
							scopedDisclaimerManager,
							formatCalculatedDisclaimer(selectedOferta),
						);
					} else {
						updateCalculatedReference(
							scopedDisclaimerManager,
							calculatedDisclamerBeforeUpdate.text,
							formatCalculatedDisclaimer(selectedOferta),
						);
					}

					ofertasService.updateDataCalculator(
						selectedOferta.marketingCode,
						product,
					);
					handleClose();
					if (financeData.dataFromOfertasLayer == true) {
						setIsLayerVisible(true);
					}
				},
			});
		}
	}, [
		calculatorResult,
		ofertasService,
		financeData.request,
		financeData.selectedOferta,
	]);

	const handleClose = () => {
		setIsFinanceLayerVisible(false);

		rawResponseRef.current = undefined;
		summariesRef.current = undefined;

		if (financeLayerRef?.current) {
			financeLayerRef?.current.close();
			return ((financeLayerRef.current as any) = null);
		}
		return null;
	};

	const CTAStyled = styled(CTA)`
		width: 32px;
		height: 32px;

		& svg {
			width: 32px;
			height: 32px;
		}
	`;

	const renderJourneyLayer = () => {
		return (
			<OfertasContext.Provider value={ofertasContext}>
				<Container
					horizontalAlign="center"
					verticalAlign="center"
					wrap={'never'}
					padding={{
						left: ContainerPadding.grid001,
						right: ContainerPadding.grid001,
						top: ContainerPadding.dynamic0100,
						bottom: ContainerPadding.dynamic0100,
					}}
					stretchContent
				>
					<Container stretchContent verticalAlign="center">
						<Text
							appearance={TokenTextAppearance.headline300}
							textAlign={TextAlignment.left}
							bold
						>
							Calcula tu cuota
						</Text>
					</Container>
					<Container stretchContent verticalAlign="center">
						<Text textAlign={TextAlignment.right}>
							<CTAStyled
								tag="button"
								emphasis="tertiary"
								onClick={handleClose}
								ariaLabel="Close pop-up"
								icon={<Close variant="default" ariaHidden />}
							/>
						</Text>
					</Container>
				</Container>

				<Container
					horizontalAlign={'center'}
					stretchContent
					padding={{
						left: ContainerPadding.grid001,
						bottom: ContainerPadding.static100,
					}}
				>
					<Text
						appearance={TokenTextAppearance.bigcopy100}
						textAlign={TextAlignment.left}
						bold
					>
						Personaliza tu financiación
					</Text>
				</Container>
				<Container
					horizontalAlign={'center'}
					stretchContent
					padding={{
						left: ContainerPadding.grid001,
						bottom: ContainerPadding.dynamic0100,
					}}
				>
					<Text
						appearance={TokenTextAppearance.bigcopy200}
						textAlign={TextAlignment.left}
						bold
					>
						{financeData.selectedOferta?.carline?.title}
						{'\u00A0'}
						{financeData.selectedOferta?.motorizacion}
					</Text>
				</Container>

				<Container
					horizontalAlign={'center'}
					padding={{
						right: ContainerPadding.grid001,
						left: ContainerPadding.grid001,
						bottom: ContainerPadding.dynamic0200,
					}}
				>
					<div id="calculator" ref={calculatorContainerRef}>
						<div className="spinner">
							<Spinner variant="x-large" />
						</div>
					</div>
				</Container>
			</OfertasContext.Provider>
		);
	};

	const renderInteractionLayer = (): any => {
		if (!financeLayerRef.current && isFinanceLayerVisible) {
			const layer = layerManager.openFocusLayer(
				renderJourneyLayer,
				{},
				{
					size: FocusLayerSizeV2.A,
					shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
				},
			);
			(financeLayerRef.current as any) = layer;
		}
		return null;
	};

	return <>{isFinanceLayerVisible && renderInteractionLayer()}</>;
};

async function addCalculatedReference(
	selectedOferta: Oferta,
	scopedDisclaimerManager: ScopedDisclaimerManager,
	calculatedLegal: string,
) {
	//Variable para obtener referencias de forma dinamica
	const getRef = (key: string) =>
		checkActiveDisclaimer(
			scopedDisclaimerManager.getFooterDisclaimers().reference,
			key,
		)?.references?.[0];

	//Guardamos la referencia a actualizar para no perderla
	const refToUpdate = getRef(selectedOferta.cuotaLegal);
	let counter = 4;

	while (counter > 0) {
		//Comprobamos si estan las referencias de los legales
		const legalRef = getRef(selectedOferta.legal);
		const cuotaRef = getRef(selectedOferta.cuotaLegal);

		//En el caso de que no esten, podemos registrar el nuevo legal
		if (legalRef === undefined && cuotaRef === undefined) {
			scopedDisclaimerManager.registerReferenceDisclaimer(
				'footer-reference',
				calculatedLegal,
				refToUpdate,
			);
			return;
		}

		// Si alguno de los dos ya existe se desregistra
		if (cuotaRef) {
			scopedDisclaimerManager.unregisterReferenceDisclaimer(
				'footer-reference',
				selectedOferta.legal,
				legalRef,
			);
		}
		if (legalRef) {
			scopedDisclaimerManager.unregisterReferenceDisclaimer(
				'footer-reference',
				selectedOferta.cuotaLegal,
				cuotaRef,
			);
		}

		//Actualizamos el contador para no generar ningun bucle ininito
		counter--;
	}

	alert(
		'Error, faltan legales en la oferta original. Cierra el pop up para recargar la pagina',
	);
	location.reload();
}

async function updateCalculatedReference(
	scopedDisclaimerManager: ScopedDisclaimerManager,
	oldLegalText: string,
	newCalculatedLegal: string,
) {
	//Variable para obtener referencias de forma dinamica
	const getRef = (key: string) =>
		checkActiveDisclaimer(
			scopedDisclaimerManager.getFooterDisclaimers().reference,
			key,
		)?.references?.[0];

	//Guardamos la referencia a actualizar para no perderla
	const refToUpdate = getRef(oldLegalText);
	let counter = 4;

	while (counter > 0) {
		//Comprobamos si esta el legal anterior
		let oldLegalNumRef = getRef(oldLegalText);

		// Si encuentra la referencia la desregistra
		if (oldLegalNumRef) {
			scopedDisclaimerManager.unregisterReferenceDisclaimer(
				'footer-reference',
				oldLegalText,
				refToUpdate,
			);
		}

		//Comprobamos si se ha desregistrado y registramos la nueva
		oldLegalNumRef = getRef(oldLegalText);
		if (oldLegalNumRef === undefined) {
			scopedDisclaimerManager.registerReferenceDisclaimer(
				'footer-reference',
				newCalculatedLegal,
				refToUpdate,
			);
			return;
		}

		counter--;
	}

	alert(
		'Error, faltan legales en la oferta original. Cierra el pop up para recargar la pagina',
	);
	location.reload();
}
